import React, { useCallback } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { isNativePlatform } from '../../utils/platform';

/**
 * Inner component that registers the service worker and shows the PWA update
 * prompt.  Only rendered on web — never inside Capacitor's native WebView.
 */
const ReloadPromptInner: React.FC = () => {
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      // Check for updates every 60 seconds
      if (registration) {
        setInterval(() => {
          registration.update().catch((_error: unknown) => {
            // SW update failures are expected during deployments when assets
            // are briefly unavailable. Silently ignore — the next poll will
            // pick up the new SW once the deploy stabilises.
          });
        }, 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  const close = useCallback(() => {
    setOfflineReady(false);
    setNeedRefresh(false);
  }, [setOfflineReady, setNeedRefresh]);

  const handleReload = useCallback(() => {
    updateServiceWorker(true);
  }, [updateServiceWorker]);

  if (!offlineReady && !needRefresh) {
    return null;
  }

  return (
    <div
      role="alert"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-pop"
    >
      <div className="flex items-center gap-3 rounded-2xl bg-white dark:bg-dark-surface shadow-lg border border-purple-200 dark:border-purple-700 px-4 py-3 text-sm">
        <span className="text-gray-700 dark:text-dark-text font-heebo">
          {offlineReady ? 'המשחק מוכן לשימוש אופליין' : 'גרסה חדשה זמינה!'}
        </span>
        {needRefresh && (
          <button
            onClick={handleReload}
            className="rounded-xl bg-kid-purple text-white px-3 py-1.5 text-sm font-bold font-heebo
                       hover:bg-purple-600 active:scale-95 transition-all"
          >
            עדכון
          </button>
        )}
        <button
          onClick={close}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-lg leading-none"
          aria-label="Close"
        >
          &times;
        </button>
      </div>
    </div>
  );
};

ReloadPromptInner.displayName = 'ReloadPromptInner';

/**
 * PWA update prompt component.
 * Shows a toast when a new version is available and lets the user reload.
 * With autoUpdate + skipWaiting, most updates happen silently.
 * This handles the edge case where the user needs to manually trigger a reload.
 *
 * Disabled when running inside a Capacitor native shell — the native WebView
 * serves assets from the bundled filesystem, not a service worker.
 */
export const ReloadPrompt: React.FC = () => {
  // Service workers are unnecessary (and can cause issues) inside Capacitor's
  // native WebView.  Skip registration entirely on native platforms.
  if (isNativePlatform()) {
    return null;
  }

  return <ReloadPromptInner />;
};

ReloadPrompt.displayName = 'ReloadPrompt';
